import React, { useState, useEffect } from 'react';
import { Transition } from '@headlessui/react';
import { CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { XMarkIcon } from '@heroicons/react/20/solid';

export default function Notify({ message, type, key, onClose }) {
    const [show, setShow] = useState(true);

    useEffect(() => {
       
        setShow(true);
        const timer = setTimeout(() => {
         
            setShow(false);
            setTimeout(() => {
                onClose(); // Call onClose callback after animation completes
            }, 300);
        }, type === 'success' ? 3500 : 7000); // Success: 3.5s, Error: 7s
        
        return () => clearTimeout(timer); // Clear timeout if component unmounts
    }, [key, type, onClose, message]);

    const getIcon = () => {
        switch (type) {
            case 'success':
                return <CheckCircleIcon aria-hidden="true" className="size-6 text-green-500" />;
            case 'error':
                return <ExclamationCircleIcon aria-hidden="true" className="size-6 text-red-500" />;
            default:
                return null;
        }
    };

    const borderColorClass = type === 'success' ? 'border-green-500' : 'border-red-500';

    const handleClose = () => {
        setShow(false);
        onClose(); // Call onClose callback when the close button is clicked
    };

    return (
        <div aria-live="assertive" className="pointer-events-none fixed inset-0 flex items-start px-4 py-6 sm:p-6 z-50">
            <div className="ml-auto flex w-full max-w-sm flex-col items-end space-y-4 drop-shadow-2xl">
                <Transition
                    show={show}
                    enter="transform transition duration-300"
                    enterFrom="translate-y-full opacity-0"
                    enterTo="translate-y-0 opacity-100"
                    leave="transform transition duration-200"
                    leaveFrom="translate-y-0 opacity-100"
                    leaveTo="translate-y-full opacity-0"
                >
                    <div
                        className={`pointer-events-auto w-full max-w-sm overflow-hidden rounded-lg bg-white shadow-xl ring-1 ring-black/5 border-l-4 ${borderColorClass}`}
                        onClick={handleClose} // Close notification on click
                    >
                        <div className="p-4">
                            <div className="flex items-start">
                                <div className="shrink-0">
                                    {getIcon()}
                                </div>

                                <div className="ml-3 w-0 flex-1">
                                    <div className="flex justify-between items-center mb-2">
                                        <p className="text-sm font-bold text-gray-900">{message.title}</p>
                                        <button
                                            type="button"
                                            onClick={handleClose}
                                            className="inline-flex rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                                        >
                                            <XMarkIcon className="size-5" aria-hidden="true" />
                                        </button>
                                    </div>
                                    <div className="text-left"> {/* Changed from text-center to text-left */}
                                        <p className="text-sm text-gray-800">{message.description}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </Transition>
            </div>
        </div>
    );
}

export const useNotifications = () => {
    const [notification, setNotification] = useState(null);
    const [notificationKey, setNotificationKey] = useState(0);

    const showSuccess = (message) => {
      
        setNotification({
            message: {
                title: 'Success',
                description: message,
            },
            type: 'success',
        });
        setNotificationKey((prevKey) => prevKey + 1);
    };

    const showError = (message) => {
       
        setNotification({
            message: {
                title: 'Error',
                description: message,
            },
            type: 'error',
        });
        setNotificationKey((prevKey) => prevKey + 1);
    };

    const clearNotification = () => {
        setNotification(null);
    };

    const NotificationComponent = () => (
        <div className="fixed top-4 right-4 w-auto max-w-md z-50">
            {notification && (
                <Notify key={notificationKey} message={notification.message} type={notification.type} onClose={clearNotification} />
            )}
        </div>
    );

    return { showSuccess, showError, clearNotification, NotificationComponent };
};
